import type { ITheme } from 'survey-core'

// Respondent "looks" for SurveyJS. The stored keys (default, modern, minimal,
// professional, playful) are survey data and stay as they are; only the
// rendering changed in the Questionnaire redesign (design-research/plan.md).

interface Palette {
  page: string
  card: string
  sunk: string
  ink: string
  ink2: string
  ink3: string
  rule: string
  ruleStrong: string
  accent: string
  accentDark: string
  onAccent: string
  error: string
  dark?: boolean
  radius?: string
  panelless?: boolean
}

function look(name: string, p: Palette): ITheme {
  const rgba = (hex: string, a: number) => {
    const n = Number.parseInt(hex.slice(1), 16)
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`
  }
  return {
    themeName: name,
    colorPalette: p.dark ? 'dark' : 'light',
    isPanelless: p.panelless ?? true,
    cssVariables: {
      '--sjs-font-family': '"Instrument Sans", ui-sans-serif, system-ui, sans-serif',
      '--sjs-font-size': '16px',
      '--sjs-base-unit': '8px',
      '--sjs-corner-radius': p.radius ?? '3px',
      '--sjs-general-backcolor': p.card,
      '--sjs-general-backcolor-dark': p.sunk,
      '--sjs-general-backcolor-dim': p.page,
      '--sjs-general-backcolor-dim-light': p.card,
      '--sjs-general-backcolor-dim-dark': p.sunk,
      '--sjs-general-forecolor': p.ink,
      '--sjs-general-forecolor-light': p.ink3,
      '--sjs-general-dim-forecolor': p.ink2,
      '--sjs-general-dim-forecolor-light': p.ink3,
      '--sjs-primary-backcolor': p.accent,
      '--sjs-primary-backcolor-light': rgba(p.accent, 0.1),
      '--sjs-primary-backcolor-dark': p.accentDark,
      '--sjs-primary-forecolor': p.onAccent,
      '--sjs-primary-forecolor-light': rgba(p.onAccent, 0.75),
      '--sjs-secondary-backcolor': p.accent,
      '--sjs-secondary-backcolor-light': rgba(p.accent, 0.1),
      '--sjs-secondary-backcolor-semi-light': rgba(p.accent, 0.25),
      '--sjs-secondary-forecolor': p.onAccent,
      '--sjs-secondary-forecolor-light': rgba(p.onAccent, 0.75),
      '--sjs-shadow-small': `0 0 0 1px ${p.ruleStrong}`,
      '--sjs-shadow-small-reset': '0 0 0 0 transparent',
      '--sjs-shadow-medium': `0 1px 0 ${rgba(p.ink, 0.06)}, 0 8px 24px -12px ${rgba(p.ink, 0.3)}`,
      '--sjs-shadow-large': `0 16px 40px -16px ${rgba(p.ink, 0.35)}`,
      '--sjs-shadow-inner': `inset 0 0 0 1px ${p.ruleStrong}`,
      '--sjs-shadow-inner-reset': 'inset 0 0 0 0 transparent',
      '--sjs-border-light': p.rule,
      '--sjs-border-default': p.ruleStrong,
      '--sjs-border-inside': rgba(p.ink, 0.08),
      '--sjs-special-red': p.error,
      '--sjs-special-red-light': rgba(p.error, 0.1),
      '--sjs-special-red-forecolor': p.onAccent,
      '--sjs-special-green': '#3F6B4D',
      '--sjs-special-green-light': 'rgba(63, 107, 77, 0.12)',
      '--sjs-special-green-forecolor': '#FAF8F3',
      '--sjs-special-blue': '#2F4FB5',
      '--sjs-special-blue-light': 'rgba(47, 79, 181, 0.12)',
      '--sjs-special-blue-forecolor': '#FAF8F3',
      '--sjs-special-yellow': '#B7791F',
      '--sjs-special-yellow-light': 'rgba(183, 121, 31, 0.14)',
      '--sjs-special-yellow-forecolor': '#17150F',
      '--sjs-article-font-xx-large-fontFamily': '"Instrument Serif", Georgia, serif',
      '--sjs-article-font-x-large-fontFamily': '"Instrument Serif", Georgia, serif',
      '--sjs-article-font-large-fontFamily': '"Instrument Serif", Georgia, serif',
      '--sjs-font-surveytitle-family': '"Instrument Serif", Georgia, serif',
      '--sjs-font-surveytitle-weight': '400',
      '--sjs-font-surveytitle-size': '40px',
      '--sjs-font-pagetitle-family': '"Instrument Serif", Georgia, serif',
      '--sjs-font-pagetitle-weight': '400',
      '--sjs-font-questiontitle-family': '"Instrument Serif", Georgia, serif',
      '--sjs-font-questiontitle-weight': '400',
      '--sjs-font-questiontitle-size': '28px',
      '--sjs-font-editorfont-size': '16px',
    },
  }
}

/** Page ground behind the runner for each look (used by SurveyRunnerComponent). */
export const lookBackgrounds: Record<
  string,
  { page: string; ink: string; muted: string; rule: string; accent: string; dark: boolean }
> = {
  default: {
    page: '#F3EFE6',
    ink: '#17150F',
    muted: '#6E685C',
    rule: '#D6CFBF',
    accent: '#C8361B',
    dark: false,
  },
  modern: {
    page: '#17150F',
    ink: '#F3EFE6',
    muted: '#B9B09C',
    rule: '#3A3730',
    accent: '#F06A4A',
    dark: true,
  },
  minimal: {
    page: '#FFFFFF',
    ink: '#111111',
    muted: '#5F5E5A',
    rule: '#E4E4E0',
    accent: '#111111',
    dark: false,
  },
  professional: {
    page: '#F4F5F8',
    ink: '#141A2E',
    muted: '#566079',
    rule: '#D6DAE4',
    accent: '#2F4FB5',
    dark: false,
  },
  playful: {
    page: '#F7EDD6',
    ink: '#2A1E0A',
    muted: '#6F5A33',
    rule: '#E2CFA3',
    accent: '#8F5E14',
    dark: false,
  },
}

export const surveyThemes: Record<string, ITheme> = {
  // Paper: the house look. Warm paper, ink, red pen.
  default: look('default', {
    page: '#F3EFE6',
    card: '#FAF8F3',
    sunk: '#EAE4D7',
    ink: '#17150F',
    ink2: '#4A463D',
    ink3: '#6E685C',
    rule: '#D6CFBF',
    ruleStrong: '#B9B09C',
    accent: '#C8361B',
    accentDark: '#A92A12',
    onAccent: '#FAF8F3',
    error: '#A92A12',
  }),
  // Night: the same sheet in reverse, for evening events and dark brands.
  modern: look('modern', {
    page: '#17150F',
    card: '#211F18',
    sunk: '#2C2A24',
    ink: '#F3EFE6',
    ink2: '#D6CFBF',
    ink3: '#B9B09C',
    rule: '#3A3730',
    ruleStrong: '#5A564B',
    accent: '#F06A4A',
    accentDark: '#E0573A',
    onAccent: '#17150F',
    error: '#F06A4A',
    dark: true,
  }),
  // Plain: white page, black ink, no color at all.
  minimal: look('minimal', {
    page: '#FFFFFF',
    card: '#FFFFFF',
    sunk: '#F2F2F0',
    ink: '#111111',
    ink2: '#3D3D3A',
    ink3: '#5F5E5A',
    rule: '#E4E4E0',
    ruleStrong: '#BDBDB8',
    accent: '#111111',
    accentDark: '#000000',
    onAccent: '#FFFFFF',
    error: '#B3261E',
    radius: '0px',
  }),
  // Ledger: cool gray paper and cobalt ink, for formal or work surveys.
  professional: look('professional', {
    page: '#F4F5F8',
    card: '#FFFFFF',
    sunk: '#E8EBF2',
    ink: '#141A2E',
    ink2: '#3A4359',
    ink3: '#566079',
    rule: '#D6DAE4',
    ruleStrong: '#AEB5C6',
    accent: '#2F4FB5',
    accentDark: '#243E91',
    onAccent: '#FFFFFF',
    error: '#B3261E',
    radius: '6px',
  }),
  // Ochre: warm yellow paper and brown ink, for informal and community surveys.
  playful: look('playful', {
    page: '#F7EDD6',
    card: '#FCF6E8',
    sunk: '#EFE0BC',
    ink: '#2A1E0A',
    ink2: '#4F3D1C',
    ink3: '#6F5A33',
    rule: '#E2CFA3',
    ruleStrong: '#C9B07A',
    accent: '#8F5E14',
    accentDark: '#744B0F',
    onAccent: '#FCF6E8',
    error: '#A92A12',
    radius: '12px',
  }),
}

// Theme labels for display
export const themeLabels: Record<string, string> = {
  default: 'Paper',
  modern: 'Night',
  minimal: 'Plain',
  professional: 'Ledger',
  playful: 'Ochre',
}

// Get theme by name
export function getTheme(themeName: string): ITheme {
  return surveyThemes[themeName] || surveyThemes.default
}

// Apply theme to survey element
export function applyThemeStyles(element: HTMLElement, themeName: string): void {
  const theme = getTheme(themeName)
  if (theme.cssVariables) {
    for (const [key, value] of Object.entries(theme.cssVariables)) {
      element.style.setProperty(key, value)
    }
  }
}
